import type { Sessao } from './auth';
import { CABECALHO_CSRF } from './auth';
import type { Falha, Resultado } from './acesso';
import { buscar } from './buscar';

/**
 * BLOCO 7 — o front do balconista: achar o produto, ver quanto ganha, registrar, desfazer.
 *
 * 🚨 A tela NUNCA calcula prêmio. Quanto a pessoa ganha vem de `/api/venda/previa`, que roda o
 * MESMO motor do registro; o registro devolve o valor gravado.
 *
 * Escrita tem três desfechos (e nenhum vira outro): CONFIRMADO (o servidor respondeu com a
 * venda), RECUSADO (o servidor disse não, com o motivo) e INCERTO (a rede caiu no meio — a venda
 * pode ter sido salva). No incerto a tela oferece "Verificar", que PERGUNTA pela chave da tentativa:
 * se já gravou, é a mesma venda; só com o servidor confirmando que não existe ela reenvia (com a
 * MESMA chave). Nunca duplica e nunca diz "recusada" sem certeza.
 */

export type ProgramaDeVenda = 'vitamina' | 'pre_vencido' | 'paradao';
export type Faixa = 'a' | 'b' | 'c' | 'pv';

export type OpcoesDeVenda = {
  readonly productId: string;
  readonly descricao: string;
  readonly codigo: string | null;
  readonly fabricante: string | null;
  readonly estoque: string | null;
  readonly pmc: string | null;
  readonly vitamina: {
    readonly faixas: readonly {
      readonly ordem: number;
      readonly preco: string;
      readonly premio: string;
    }[];
  } | null;
  readonly preVencido: {
    readonly venderPor: string | null;
    readonly premioNaBase: string;
    readonly lotes: readonly {
      readonly codigo: string | null;
      readonly validade: string | null;
      readonly quantidade: string | null;
    }[];
    readonly validadeAte: string;
  } | null;
  readonly paradao: { readonly venderPor: string | null; readonly premioNaBase: string } | null;
};

export type ResultadoDaBusca = {
  readonly productId: string;
  readonly descricao: string;
  readonly codigo: string | null;
  readonly fabricante: string | null;
  readonly estoque: string | null;
  readonly programas: readonly ProgramaDeVenda[];
  /** Só na busca por sintoma: a indicação (da bula, pelos dados do 1.0). */
  readonly indicacao?: string;
};

export type Previa = {
  readonly precoUnitario: string;
  readonly premioUnitario: string;
  readonly premioTotal: string;
  readonly explicacao: string;
  readonly bloqueio: string | null;
};

export type VendaRegistrada = {
  readonly id: string;
  readonly programa: ProgramaDeVenda;
  readonly productId: string;
  readonly descricao: string;
  readonly quantidade: number;
  readonly precoUnitario: string;
  readonly premioUnitario: string;
  readonly premioTotal: string;
  readonly explicacao: string;
  readonly registradoEm: string;
  readonly repetido: boolean;
};

export type MinhaVenda = {
  readonly id: string;
  readonly programa: ProgramaDeVenda;
  readonly productId: string;
  readonly descricao: string;
  readonly quantidade: number;
  readonly precoUnitario: string;
  readonly premioTotal: string;
  readonly registradoEm: string;
  readonly status: 'ativo' | 'cancelado';
  readonly canceladoEm: string | null;
  readonly faixa: string;
  readonly loteCodigo: string | null;
  readonly validade: string | null;
  readonly observacao: string | null;
};

export type ResumoDoBalconista = {
  readonly hoje: string;
  readonly semana: { readonly de: string; readonly ate: string };
  readonly itensHoje: number;
  readonly premiacaoHoje: string;
  readonly itensSemana: number;
  readonly premiacaoPrevistaSemana: string;
  readonly metaSemanal: number;
  readonly ultimas: readonly MinhaVenda[];
  readonly maisVendidos: readonly {
    readonly productId: string;
    readonly descricao: string;
    readonly codigo: string | null;
    readonly vezes: number;
  }[];
  readonly ultimaVenda: MinhaVenda | null;
  readonly programas: Readonly<Record<ProgramaDeVenda, boolean>>;
  /** Modo manutenção ligado: a mensagem (o servidor recusa os lançamentos). */
  readonly manutencao: string | null;
  /** Venda bloqueada hoje pelo Giro (o auditor achou o produto do "não achei"). */
  readonly bloqueioGiro?: string | null;
  /**
   * A meta da semana como o CHEQUE conta (itens líquidos: bateu com o HOS, menos devolução e
   * cancelamento). É esta que enche a barra e dispara "META DA SEMANA BATIDA!" — `itensSemana`
   * é só o lançado (revisão 360, D6).
   */
  readonly metaDoCheque: {
    readonly meta: number;
    readonly confirmados: number;
    readonly batida: boolean;
    readonly faltam: number;
  };
};

export type PedidoDeVenda = {
  readonly chaveIdempotencia: string;
  readonly productId: string;
  readonly programa: ProgramaDeVenda;
  readonly quantidade: number;
  readonly faixa: Faixa;
  readonly precoUnitario?: string | null;
  readonly loteCodigo?: string | null;
  readonly validade?: string | null;
  readonly evidenciaId?: string | null;
  readonly observacao?: string | null;
};

export type DesfechoDaVenda =
  | { readonly tipo: 'confirmado'; readonly venda: VendaRegistrada }
  | { readonly tipo: 'recusado'; readonly mensagem: string; readonly semSessao?: boolean }
  | { readonly tipo: 'incerto' };

async function mensagem(r: Response): Promise<string> {
  try {
    return ((await r.json()) as { error?: { message?: string } }).error?.message ?? '';
  } catch {
    return '';
  }
}

async function ler<T>(caminho: string): Promise<Resultado<T>> {
  let r: Response;
  try {
    r = await buscar(caminho, {
      credentials: 'same-origin',
      headers: { accept: 'application/json' },
    });
  } catch {
    return { ok: false, falha: { tipo: 'offline' } };
  }
  if (r.ok) return { ok: true, dados: (await r.json()) as T };
  const m = await mensagem(r);
  const falha: Falha =
    r.status === 401
      ? { tipo: 'sem-sessao' }
      : r.status === 403
        ? { tipo: 'sem-acesso' }
        : r.status === 404 || r.status === 400
          ? { tipo: 'invalido', mensagem: m }
          : { tipo: 'inesperado', codigo: r.headers.get('x-request-id') };
  return { ok: false, falha };
}

export const encontrarProduto = (codigo: string) =>
  ler<OpcoesDeVenda>(`/api/venda/produto?codigo=${encodeURIComponent(codigo.trim())}`);

export const buscarProduto = (termo: string) =>
  ler<{ itens: readonly ResultadoDaBusca[] }>(
    `/api/venda/busca?q=${encodeURIComponent(termo.trim())}`,
  );

/** Busca por sintoma (dados e critério do 1.0): casa pela indicação, não pelo nome solto. */
export const buscarPorSintoma = (termo: string) =>
  ler<{
    itens: readonly {
      productId: string;
      descricao: string;
      codigo: string | null;
      programas: readonly ProgramaDeVenda[];
      indicacao: string;
      similaridade: string;
    }[];
  }>(`/api/venda/sintoma?q=${encodeURIComponent(termo.trim())}`);

/** Opções de um produto já conhecido (ex.: escolhido na busca ou "repetir última"). */
export const opcoesDoProduto = (productId: string) =>
  ler<OpcoesDeVenda>(`/api/venda/produto?id=${encodeURIComponent(productId)}`);

export function previa(q: {
  productId: string;
  programa: ProgramaDeVenda;
  faixa: Faixa;
  preco?: string | null;
  quantidade: number;
}) {
  const p = new URLSearchParams({
    productId: q.productId,
    programa: q.programa,
    faixa: q.faixa,
    quantidade: String(q.quantidade),
  });
  if (q.preco) p.set('preco', q.preco);
  return ler<Previa>(`/api/venda/previa?${p.toString()}`);
}

export const resumoDoBalconista = () => ler<ResumoDoBalconista>('/api/balconista/resumo');

/**
 * A META DA EQUIPE com brinde (1.0: "Meta da equipe" do balconista). Só o AGREGADO da equipe:
 * itens confirmados com o HOS de todos somados — nunca o número de um colega.
 */
export interface MetaDaEquipe {
  readonly periodo: 'mes' | 'semana';
  readonly de: string;
  readonly ate: string;
  readonly alvo: number;
  readonly feitos: number;
  readonly pct: number;
  readonly faltam: number;
  readonly batida: boolean;
  readonly brinde: string | null;
}
export const lerMetaDaEquipe = () =>
  ler<{ readonly meta: MetaDaEquipe | null }>('/api/balconista/meta-da-equipe');

export const minhasVendas = (periodo: 'hoje' | 'semana' | 'semana_passada' | 'mes') =>
  ler<{ de: string; ate: string; vendas: readonly MinhaVenda[] }>(
    `/api/vendas/minhas?periodo=${periodo}`,
  );

/** Uma chave por TENTATIVA de venda: nasce quando a pessoa aperta "Registrar", não antes. */
export function novaChave(): string {
  // `crypto.randomUUID` só existe em página segura (HTTPS); na rede da loja por http ele some e o
  // botão quebraria calado. `getRandomValues` existe em qualquer página.
  const b = crypto.getRandomValues(new Uint8Array(16));
  b[6] = ((b[6] ?? 0) & 0x0f) | 0x40;
  b[8] = ((b[8] ?? 0) & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export async function registrarVenda(
  sessao: Sessao,
  pedido: PedidoDeVenda,
): Promise<DesfechoDaVenda> {
  let r: Response;
  const controle = new AbortController();
  const limite = window.setTimeout(() => controle.abort(), 20_000);
  try {
    r = await fetch('/api/vendas', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', [CABECALHO_CSRF]: sessao.csrfToken },
      body: JSON.stringify(pedido),
      signal: controle.signal,
    });
  } catch {
    // A rede caiu ou demorou: o servidor pode ter gravado. Não é recusa.
    return { tipo: 'incerto' };
  } finally {
    window.clearTimeout(limite);
  }
  if (r.ok) return { tipo: 'confirmado', venda: (await r.json()) as VendaRegistrada };
  if (r.status >= 500) return { tipo: 'incerto' };
  if (r.status === 401)
    return { tipo: 'recusado', mensagem: 'Sua sessão acabou. Entre de novo.', semSessao: true };
  const m = await mensagem(r);
  if (r.status === 403)
    return { tipo: 'recusado', mensagem: m || 'Sem permissão para registrar venda.' };
  return { tipo: 'recusado', mensagem: m || 'A venda não foi aceita.' };
}

/**
 * "Verificar" de uma venda INCERTA (caça a bugs 26/09, item 31). Antes reenviava o registro: se
 * entre o envio e o "Verificar" o Giro bloqueou a venda (ou qualquer outra recusa), a tela dizia
 * "recusada" para uma venda que JÁ estava gravada — e a pessoa lançava de novo (duplicada).
 *
 * Agora PERGUNTA ao servidor pela chave da tentativa:
 *   - achou → CONFIRMADO (a mesma venda, nada é reenviado);
 *   - 404 (o servidor confirma que não existe) → aí sim reenvia com a MESMA chave; o desfecho do
 *     reenvio é o que vale (se ela entrar no meio, a chave única devolve a mesma venda);
 *   - qualquer outra coisa (rede, 5xx, sessão) → continua INCERTO. Nunca "recusada" sem certeza.
 */
export async function verificarVenda(
  sessao: Sessao,
  pedido: PedidoDeVenda,
): Promise<DesfechoDaVenda> {
  let r: Response;
  try {
    r = await buscar(`/api/vendas/tentativa/${encodeURIComponent(pedido.chaveIdempotencia)}`, {
      credentials: 'same-origin',
      headers: { accept: 'application/json' },
    });
  } catch {
    return { tipo: 'incerto' };
  }
  if (r.ok) {
    try {
      return { tipo: 'confirmado', venda: (await r.json()) as VendaRegistrada };
    } catch {
      return { tipo: 'incerto' };
    }
  }
  if (r.status === 404) return registrarVenda(sessao, pedido);
  return { tipo: 'incerto' };
}

export async function cancelarVenda(
  sessao: Sessao,
  id: string,
  motivo?: string,
): Promise<{ ok: true } | { ok: false; mensagem: string }> {
  let r: Response;
  try {
    r = await fetch(`/api/vendas/${id}/cancelar`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', [CABECALHO_CSRF]: sessao.csrfToken },
      body: JSON.stringify(motivo ? { motivo } : {}),
    });
  } catch {
    return { ok: false, mensagem: 'Sem conexão. A venda continua registrada — tente de novo.' };
  }
  if (r.ok) return { ok: true };
  return { ok: false, mensagem: (await mensagem(r)) || 'Não foi possível desfazer.' };
}

export async function enviarFoto(
  sessao: Sessao,
  foto: Blob,
): Promise<{ ok: true; id: string } | { ok: false; mensagem: string }> {
  let r: Response;
  try {
    r = await fetch('/api/evidencias', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'image/jpeg', [CABECALHO_CSRF]: sessao.csrfToken },
      body: foto,
    });
  } catch {
    return { ok: false, mensagem: 'Sem conexão para enviar a foto. Tente de novo.' };
  }
  if (r.ok) return { ok: true, id: ((await r.json()) as { id: string }).id };
  if (r.status === 413) return { ok: false, mensagem: 'Foto maior que 8 MB. Tire de novo.' };
  return { ok: false, mensagem: (await mensagem(r)) || 'A foto não foi aceita.' };
}

/* ------------------------------------------------------------------ *
 * Formatos
 * ------------------------------------------------------------------ */

export const ROTULO_DO_PROGRAMA: Record<ProgramaDeVenda, string> = {
  vitamina: 'vitamina',
  pre_vencido: 'pré-vencido',
  paradao: 'paradão',
};

export const SIGLA_DO_PROGRAMA: Record<ProgramaDeVenda, string> = {
  vitamina: 'VIT',
  pre_vencido: 'PV',
  paradao: 'PAR',
};

/** '2026-09-24T12:14:00Z' → '09:14' no horário de Brasília. */
export function hora(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  });
}

/** '2026-11-30' → '30/11/2026'. */
export function dataCurta(dia: string): string {
  return dia.split('-').reverse().join('/');
}

/** '18.000' → '18'; '2.500' → '2,5'. */
export function unidades(q: string | null): string {
  if (q === null) return '—';
  return String(Number(q)).replace('.', ',');
}

/* ------------------------------------------------------------------ *
 * Tela 37 — meu desempenho
 * ------------------------------------------------------------------ */

export type JanelaDoDesempenho = 'hoje' | 'semana' | 'mes';

export type IndicadorDoHos =
  | { readonly disponivel: false; readonly porque: string }
  | {
      readonly disponivel: true;
      readonly valor: string;
      readonly posicao: number;
      readonly de: number;
    };

export type RankingDoPrograma =
  | { readonly disponivel: false; readonly porque: string }
  | {
      readonly disponivel: true;
      readonly posicao: number | null;
      readonly de: number;
      readonly minhaQuantidade: number;
      /** Só a própria pessoa, se estiver no top 3 — sem percentual (dele se tirava o nº do 1º). */
      readonly top3: readonly {
        readonly posicao: number;
        readonly nome: string;
        readonly eu: boolean;
      }[];
    };

export type Desempenho = {
  readonly janela: JanelaDoDesempenho;
  readonly de: string;
  readonly ate: string;
  readonly nome: string;
  readonly balconistasAtivos: number;
  readonly atualizadoEm: string;
  readonly hos: {
    readonly ticketMedio: IndicadorDoHos;
    readonly itensPorCupom: IndicadorDoHos;
    readonly itensDiferentesPorCupom: IndicadorDoHos;
  };
  readonly rankings: {
    readonly geral: RankingDoPrograma;
    readonly vitamina: RankingDoPrograma;
    readonly preVencido: RankingDoPrograma;
    readonly paradao: RankingDoPrograma;
  };
};

export const meuDesempenho = (janela: JanelaDoDesempenho) =>
  ler<Desempenho>(`/api/balconista/desempenho?janela=${janela}`);

/* ------------------------------------------------------------------ *
 * O que está pagando (vitrine do balconista): sem custo, sem lote
 * ------------------------------------------------------------------ */

type ProdutoQuePaga = {
  readonly productId: string;
  readonly descricao: string;
  readonly codigo: string | null;
  readonly fabricante: string | null;
};
export type VitaminaQuePaga = ProdutoQuePaga & {
  readonly estoque: string | null;
  readonly cards: readonly { readonly aPartirDe: string; readonly premio: string }[];
};
/** Os dois cards do 1.0: A no preço base, B no PMC do cadastro. */
export type CardsPorBase = {
  readonly venderPor: string | null;
  readonly premio: string;
  readonly pmc: string | null;
  readonly premioNoPmc: string | null;
};
export type PreVencidoQuePaga = ProdutoQuePaga &
  CardsPorBase & {
    readonly quantidade: string | null;
    readonly venceEm: string | null;
  };
export type ParadaoQuePaga = ProdutoQuePaga &
  CardsPorBase & {
    readonly estoque: string | null;
  };
type Liberado<T> =
  { readonly liberado: true; readonly itens: readonly T[] } | { readonly liberado: false };
type LiberadoPorBase<T> =
  | {
      readonly liberado: true;
      readonly itens: readonly T[];
      readonly regra: ParametrosPorBase | null;
    }
  | { readonly liberado: false };
export type OQueEstaPagando = {
  readonly atualizadoEm: string;
  readonly vitamina: Liberado<VitaminaQuePaga>;
  readonly preVencido: LiberadoPorBase<PreVencidoQuePaga>;
  readonly paradao: LiberadoPorBase<ParadaoQuePaga>;
};
export const oQueEstaPagando = () => ler<OQueEstaPagando>('/api/balconista/pagando');

export type VencendoNaSemana = {
  readonly itens: readonly ({
    readonly productId: string;
    readonly descricao: string;
    readonly diasParaVencer: number;
    readonly quantidade: string | null;
  } & CardsPorBase)[];
};
export const vencendoNaSemana = () => ler<VencendoNaSemana>('/api/balconista/vencendo');

/**
 * O rótulo do "Vencendo esta semana". Negativo = o lote JÁ venceu neste mês (ainda vale até o fim
 * do mês, caça a bugs 26/09 item 33) — antes virava "vence hoje", escondendo o mais urgente.
 */
export function quandoVence(d: number): string {
  if (d < -1) return `venceu há ${String(-d)} dias`;
  if (d === -1) return 'venceu ontem';
  if (d === 0) return 'vence hoje';
  if (d === 1) return 'vence amanhã';
  return `vence em ${String(d)} dias`;
}

export type ParametrosPorBase = {
  readonly premioBase: string;
  readonly pctDoAumento: string;
  readonly teto: string;
};
export type RegrasParaOBalconista = {
  readonly metaSemanal: number;
  readonly programas: {
    readonly vitamina: boolean;
    readonly preVencido: boolean;
    readonly paradao: boolean;
  };
  readonly preVencido: ParametrosPorBase | null;
  readonly paradao: ParametrosPorBase | null;
};
export const regrasDaPremiacao = () => ler<RegrasParaOBalconista>('/api/balconista/regras');

/* ------------------------------------------------------------------ *
 * Meu painel (dashboard do balconista): médias e POSIÇÕES da própria pessoa
 * ------------------------------------------------------------------ */

export type IndicadorDoMeuPainel =
  | { readonly disponivel: false; readonly porque: string }
  | {
      readonly disponivel: true;
      readonly valor: string;
      readonly posicao: number;
      readonly de: number;
    };
export type PosicaoNoRanking = { readonly posicao: number | null; readonly de: number };
export type MeuPainel = {
  readonly janela: JanelaDoDesempenho;
  readonly de: string;
  readonly ate: string;
  readonly nome: string;
  readonly atualizadoEm: string;
  readonly tickets: Record<
    | 'geral'
    | 'itensPorCupom'
    | 'produtosPorCupom'
    | 'porVenda'
    | 'vitamina'
    | 'preVencido'
    | 'paradao'
    | 'entrega',
    IndicadorDoMeuPainel
  >;
  /** Sem a quantidade: × ticket da entrega = total das entregas (dono, 27/09/2026). */
  readonly entregas: { readonly porDia: number | null };
  readonly rankings: Record<
    'vendas' | 'vitamina' | 'preVencido' | 'paradao' | 'entregas',
    PosicaoNoRanking
  >;
  readonly parabens: readonly string[];
  readonly evolucao: {
    readonly semana: MarcaParaBater;
    readonly mes: MarcaParaBater & { readonly serie: readonly DiaDaSerie[] };
  } | null;
  readonly metas: Record<ChaveDoTicket, MetaDoTicket> | null;
};
export type ChaveDoTicket =
  | 'geral'
  | 'itensPorCupom'
  | 'produtosPorCupom'
  | 'porVenda'
  | 'vitamina'
  | 'preVencido'
  | 'paradao'
  | 'entrega';
/** Só o ticket médio — a contagem de clientes não vem (clientes × ticket = total vendido). */
export type TicketDoPeriodo = { readonly ticket: string | null };
export type MarcaParaBater = {
  readonly atual: { readonly de: string; readonly ate: string };
  readonly anterior: { readonly de: string; readonly ate: string };
  readonly ateAgora: TicketDoPeriodo;
  readonly anteriorAteMesmoPonto: TicketDoPeriodo;
  readonly anteriorInteiro: TicketDoPeriodo;
  // sem "% já feito" nem "quanto falta para passar" (dono, 27/09/2026): davam o total vendido
};
export type DiaDaSerie = {
  readonly dia: number;
  readonly ticketAtual: string | null;
  readonly ticketAnterior: string | null;
};
export type MetaDoTicket =
  | { readonly situacao: 'sem_base' }
  | {
      readonly situacao: 'com_base';
      readonly base: string;
      readonly atual: string | null;
      readonly variacaoPct: number | null;
      readonly alvos: readonly { readonly pct: 5 | 10 | 15; readonly valor: string }[];
      readonly batidas: number;
    };
export const meuPainel = (janela: JanelaDoDesempenho) =>
  ler<MeuPainel>(`/api/balconista/painel?janela=${janela}`);

/* Minha margem e meu desconto (dono, 28/09): só os números da pessoa, com a cor da faixa. */
export type CorDaFaixa = 'vermelho' | 'amarelo' | 'azul' | 'verde';
export interface IndicadorDaFaixa {
  readonly valor: number | null;
  readonly cor: CorDaFaixa | null;
  readonly proxima: CorDaFaixa | null;
  readonly falta: number | null;
}
export interface MinhaMargem {
  readonly de: string;
  readonly ate: string;
  readonly margem: IndicadorDaFaixa;
  readonly desconto: IndicadorDaFaixa;
  readonly descontoGenerico: IndicadorDaFaixa;
  readonly cupons: number;
  readonly ticketMedio: string | null;
  readonly itens: number;
  readonly itensComCusto: number;
  readonly faixas: {
    readonly margem: { readonly amarelo: number; readonly azul: number; readonly verde: number };
    readonly desconto: { readonly amarelo: number; readonly vermelho: number };
    readonly descontoGenerico: { readonly amarelo: number; readonly vermelho: number };
  };
}
export const lerMinhaMargem = (janela: JanelaDoDesempenho) =>
  ler<MinhaMargem>(`/api/balconista/minha-margem?janela=${janela}`);

/* ------------------------------------------------------------------ *
 * Tela 10B — ranking (1.0: /api/ranking)
 * ------------------------------------------------------------------ */

export type PeriodoDoRanking = 'hoje' | 'semana' | 'semana_passada' | 'mes' | 'mes_passado';
export type LinhaDoRanking = {
  readonly posicao: number;
  readonly accountId: string;
  readonly codigo: string;
  readonly nome: string;
  readonly vitaminas: number;
  readonly preVencidos: number;
  readonly paradao: number;
  readonly itens: number;
  /** null = premiação de outra pessoa (a balconista não vê). */
  readonly valor: string | null;
  /** A premiação separada por programa (mesma regra de quem vê). */
  readonly valorVitamina: string | null;
  readonly valorPreVencido: string | null;
  readonly valorParadao: string | null;
  readonly eu: boolean;
};
export type Ranking = {
  readonly de: string;
  readonly ate: string;
  readonly metaSemanal: number;
  readonly linhas: readonly LinhaDoRanking[];
};

export const lerRanking = (periodo: PeriodoDoRanking | { de: string; ate: string }) =>
  ler<Ranking>(
    typeof periodo === 'string'
      ? `/api/ranking?periodo=${periodo}`
      : `/api/ranking?de=${periodo.de}&ate=${periodo.ate}`,
  );

/* ------------------------------------------------------------------ *
 * Tela 09 — vendas da equipe (admin)
 * ------------------------------------------------------------------ */

export type StatusDaVenda =
  | 'confirmado'
  | 'aguarda_hos'
  | 'nao_encontrado'
  | 'dia_errado'
  | 'lote_mesmo_dia'
  | 'nao_premia'
  | 'cancelada'
  | 'desfeita'
  | 'corrigida';

/** Fora da conta: cancelada, desfeita, ou a original de uma correção (a nova a substitui). */
export const foraDaConta = (s: StatusDaVenda): boolean =>
  s === 'cancelada' || s === 'desfeita' || s === 'corrigida';

export type VendaDaEquipe = {
  readonly id: string;
  readonly registradoEm: string;
  readonly dia: string;
  readonly accountId: string;
  readonly balconista: string;
  readonly codigoBalconista: string;
  readonly productId: string;
  readonly produto: string;
  readonly codigo: string | null;
  readonly programa: ProgramaDeVenda;
  readonly faixa: string;
  readonly quantidade: number;
  readonly precoUnitario: string;
  readonly premioTotal: string;
  readonly status: StatusDaVenda;
};

export type VendasDaEquipe = {
  readonly vendas: readonly VendaDaEquipe[];
  readonly resumo: {
    readonly itens: number;
    readonly valorVendido: string;
    readonly premiacao: string;
    readonly mediaPorVenda: string | null;
    readonly balconistas: number;
    readonly balconistasAtivos: number;
    readonly aguardandoHos: number;
  };
  readonly porDia: readonly { readonly dia: string; readonly itens: number }[];
  readonly porHora: readonly { readonly hora: number; readonly itens: number }[];
  readonly anterior: { readonly itens: number; readonly valorVendido: string } | null;
};

export type DetalheDaVenda = {
  readonly id: string;
  readonly registradoEm: string;
  readonly dia: string;
  readonly accountId: string;
  readonly balconista: string;
  readonly codigoBalconista: string;
  readonly produto: string;
  readonly codigo: string | null;
  readonly ean: string | null;
  readonly fabricante: string | null;
  readonly programa: ProgramaDeVenda;
  readonly faixa: string;
  readonly quantidade: number;
  readonly precoUnitario: string;
  readonly premioUnitario: string;
  readonly premioTotal: string;
  readonly explicacao: string;
  readonly loteCodigo: string | null;
  readonly validade: string | null;
  readonly evidenciaId: string | null;
  readonly observacao: string | null;
  readonly status: 'ativo' | 'cancelado';
  readonly corrigeId: string | null;
  readonly substituidaPor: string | null;
  readonly historico: readonly { readonly em: string; readonly texto: string }[];
};

export function lerVendasDaEquipe(f: {
  de: string;
  ate: string;
  pessoa?: string | null;
  programa?: ProgramaDeVenda | null;
  status?: StatusDaVenda | null;
  busca?: string;
}) {
  const p = new URLSearchParams({ de: f.de, ate: f.ate });
  if (f.pessoa) p.set('pessoa', f.pessoa);
  if (f.programa) p.set('programa', f.programa);
  if (f.status) p.set('status', f.status);
  if (f.busca?.trim()) p.set('busca', f.busca.trim());
  return ler<VendasDaEquipe>(`/api/vendas?${p.toString()}`);
}

export const lerDetalheDaVenda = (id: string) => ler<DetalheDaVenda>(`/api/vendas/${id}`);

export const ROTULO_DO_STATUS: Record<StatusDaVenda, string> = {
  confirmado: 'Confirmado',
  aguarda_hos: 'Aguarda HOS',
  nao_encontrado: 'Não encontrado',
  dia_errado: 'Dia errado',
  lote_mesmo_dia: 'Lote do mesmo dia',
  nao_premia: 'Não premia',
  cancelada: 'Cancelada',
  desfeita: 'Desfeita',
  corrigida: 'Corrigida',
};

/** A faixa gravada, em português: 'a' → MÁX, 'b' → MÍN, 'c' → Outro valor, 'pv' → Vender por/valor. */
export function rotuloDaFaixa(programa: ProgramaDeVenda, faixa: string): string {
  if (programa === 'vitamina') return faixa === 'a' ? 'MÁX' : faixa === 'b' ? 'MÍN' : 'Outro valor';
  return 'Valor informado';
}

/**
 * Corrigir (quantidade, faixa, preço, dia) ou restaurar uma cancelada. A venda original não se
 * reescreve: nasce uma venda nova ligada a ela, com o prêmio calculado pelo servidor.
 */
export async function corrigirVenda(
  sessao: Sessao,
  id: string,
  corpo: {
    readonly quantidade?: number;
    readonly faixa?: 'a' | 'b' | 'c' | 'pv';
    readonly precoUnitario?: string | null;
    readonly dia?: string;
    readonly motivo: string;
  },
): Promise<
  | {
      ok: true;
      dados: {
        id: string;
        modo: 'correcao' | 'restauracao';
        premioAntes: string;
        premioDepois: string;
      };
    }
  | { ok: false; mensagem: string }
> {
  let r: Response;
  try {
    r = await fetch(`/api/vendas/${id}/corrigir`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', [CABECALHO_CSRF]: sessao.csrfToken },
      body: JSON.stringify(corpo),
    });
  } catch {
    return {
      ok: false,
      mensagem:
        'Sem conexão — não dá para saber se corrigiu. Recarregue a venda e confira antes de repetir.',
    };
  }
  if (r.ok) return { ok: true, dados: (await r.json()) as never };
  return { ok: false, mensagem: (await mensagem(r)) || 'Não foi possível corrigir.' };
}

/** Conquistas, nível e sequência (a gamificação do 1.0), derivadas das próprias vendas. */
export interface Conquistas {
  readonly nivel: {
    readonly nome: string;
    readonly proximo: { readonly nome: string; readonly faltam: number } | null;
  };
  readonly itens: number;
  readonly sequencia: { readonly atual: number; readonly maior: number };
  readonly diasAtivos: number;
  readonly medalhas: readonly {
    readonly codigo: string;
    readonly nome: string;
    readonly descricao: string;
    readonly ganha: boolean;
  }[];
}
export const lerConquistas = () => ler<Conquistas>('/api/balconista/conquistas');
